import React from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { cn } from '@/lib/utils';
import ClientCard from './ClientCard';
import type { ClientDisplayData, ClientFullConfigData } from '@/pages/ClientsPage';

interface ClientsListProps {
  clients: ClientDisplayData[];
  isLoading: boolean;
  error: Error | null;
  editingClientId: string | null;
  editingClientData: ClientFullConfigData | null;
  isLoadingDetails: boolean;
  detailError: Error | null;
  updateMutation: {
    isPending: boolean;
    error: Error | null;
  };
  selectedClients: string[];
  setSelectedClients: React.Dispatch<React.SetStateAction<string[]>>;
  multiSelectEnabled: boolean;
  onSaveEdit: (formData: ClientFullConfigData) => Promise<void>;
  onCancelEdit: () => void;
  onEditStart: (clientId: string) => void;
  onRemove: (clientId: string) => Promise<void>;
  gridContainerRef: React.RefObject<HTMLDivElement>;
}

export const ClientsList: React.FC<ClientsListProps> = ({
  clients,
  isLoading,
  error,
  editingClientId,
  editingClientData,
  isLoadingDetails,
  detailError,
  updateMutation,
  selectedClients,
  setSelectedClients,
  multiSelectEnabled,
  onSaveEdit,
  onCancelEdit,
  onEditStart,
  onRemove,
  gridContainerRef,
}) => {
  if (isLoading) {
    return <p className="text-center text-muted-foreground">Loading clients...</p>;
  }

  if (error) {
    return <p className="text-center text-destructive">Error loading clients: {error.message}</p>;
  }

  if (clients.length === 0) {
    return (
      <p className="text-center text-muted-foreground mt-4">
        No clients found. Click the '+' button to add your first client.
      </p>
    );
  }

  return (
    <LayoutGroup>
      <motion.div
        ref={gridContainerRef}
        layout
        className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pb-4")}
        transition={{ type: 'spring', stiffness: 500, damping: 35 }}
      >
        <AnimatePresence>
          {clients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              clientId={client.id}
              isEditing={editingClientId === client.id}
              initialClientDataForEdit={editingClientId === client.id ? editingClientData : null}
              onSaveEdit={onSaveEdit}
              onCancelEdit={onCancelEdit}
              validationError={editingClientId === client.id && updateMutation.error ? updateMutation.error.message : null}
              onEditStart={onEditStart}
              onRemove={onRemove}
              selectedClients={selectedClients}
              setSelectedClients={setSelectedClients}
              multiSelectEnabled={multiSelectEnabled}
              formLoading={(editingClientId === client.id && isLoadingDetails) || (editingClientId === client.id && updateMutation.isPending)}
              formError={editingClientId === client.id && detailError ? detailError.message : null}
            />
          ))}
        </AnimatePresence>
      </motion.div>
    </LayoutGroup>
  );
};
